import { Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DateTimeUtility } from '@core/utils';
import { ScheduleColumn, ScheduleItem } from '@core/types';
import { IconComponent } from '@shared/components/ui';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

@Component({
  selector: 'app-schedule-view',
  templateUrl: './schedule-view.component.html',
  imports: [IconComponent, TranslateModule, NgxSkeletonLoaderModule],
  styleUrls: ['./schedule-view.component.scss'],
})
export class ScheduleViewComponent implements OnInit, OnChanges {
  @Input() startTime = '09:00';
  @Input() columns: ScheduleColumn[] = [];
  @Input() scheduledItems: ScheduleItem[] = [];
  @Input() selectedDate?: string; // Optional input for initial date
  @Input() hideEmptySlots = false; // Hide empty time slots
  @Input() isLoading = true; // Flag to indicate loading state
  @Output() dateChanged = new EventEmitter<string>();
  @Output() itemClicked = new EventEmitter<{ item: ScheduleItem; column: ScheduleColumn }>();
  @ViewChild('slotsContainer', { static: false }) slotsContainer!: ElementRef;
  timeSlabs: string[];
  filteredTimeSlabs: string[];
  currentDate: string;
  readonly SLOT_HEIGHT = 40;
  private readonly _translateService = inject(TranslateService);

  constructor() {
    this.timeSlabs = DateTimeUtility.timeSlotsWith15MinInterval();
    this.filteredTimeSlabs = this._getFilteredTimeSlabs();
    this.currentDate = this.selectedDate || this._getTodayDateString();
  }

  get currentDateDisplay(): string {
    const date = new Date(this.currentDate);
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = this._translateService.instant(dayNames[date.getDay()]);

    // Format the date as DD.MM.YYYY
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${dayName}, ${day}.${month}.${year}`;
  }

  ngOnInit() {
    this.filteredTimeSlabs = this._getFilteredTimeSlabs();
    if (this.selectedDate) {
      this.currentDate = this.selectedDate;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedDate'] && this.selectedDate) {
      this.currentDate = this.selectedDate;
    }

    // Always recalculate after any input change
    if (changes['startTime'] || changes['hideEmptySlots'] || changes['scheduledItems'] || changes['selectedDate']) {
      // Use setTimeout to ensure currentDate is updated first
      setTimeout(() => {
        this.filteredTimeSlabs = this._getFilteredTimeSlabs();
      });
    }
  }

  navigateDate(direction: number): void {
    const currentDate = new Date(this.currentDate);
    currentDate.setDate(currentDate.getDate() + direction);
    this.currentDate = this._formatDateString(currentDate);

    // Recalculate filtered time slabs after date change
    this.filteredTimeSlabs = this._getFilteredTimeSlabs();

    this.dateChanged.emit(this.currentDate);
  }

  getItemsForColumn(columnId: number): ScheduleItem[] {
    return this.scheduledItems.filter((item) => item.columnId === columnId && item.date === this.currentDate);
  }

  hasItemsForDate(): boolean {
    return this.scheduledItems.some((item) => item.date === this.currentDate);
  }

  hasItemsForColumn(columnId: number): boolean {
    return this.getItemsForColumn(columnId).length > 0;
  }

  onSlotClick(item: ScheduleItem, column: ScheduleColumn): void {
    this.itemClicked.emit({ item, column });
  }

  getDisplayTime(time: string): string {
    return this._normalizeTimeFormat(time);
  }

  getSlotPosition(item: ScheduleItem): { top: string; height: string } {
    const startIndex = this._getTimeSlotIndex(item.startTime);
    const endIndex = this._getTimeSlotIndex(item.endTime);

    const top = startIndex * this.SLOT_HEIGHT + 10;
    const duration = Math.max(1, endIndex - startIndex);
    const height = duration * this.SLOT_HEIGHT - 10;

    return {
      top: `${top}px`,
      height: `${Math.max(60, height)}px`,
    };
  }

  private _getFilteredTimeSlabs(): string[] {
    const normalizedStartTime = this._normalizeTimeFormat(this.startTime);
    const startIndex = this.timeSlabs.findIndex((time) => time === normalizedStartTime);

    if (startIndex === -1) {
      console.warn(`Start time ${this.startTime} not found in time slots, using full day`);
      return this.timeSlabs;
    }

    let reorderedSlabs = [...this.timeSlabs.slice(startIndex), ...this.timeSlabs.slice(0, startIndex)];

    // If hideEmptySlots is true, filter out time slots with no items across all columns
    if (this.hideEmptySlots) {
      reorderedSlabs = reorderedSlabs.filter((timeSlot) => {
        return this.scheduledItems.some((item) => item.date === this.currentDate && this._isTimeInRange(timeSlot, item.startTime, item.endTime));
      });
    }

    return reorderedSlabs;
  }

  private _isTimeInRange(timeSlot: string, startTime: string, endTime: string): boolean {
    const slotMinutes = this._timeToMinutes(timeSlot);
    const startMinutes = this._timeToMinutes(this._normalizeTimeFormat(startTime));
    const endMinutes = this._timeToMinutes(this._normalizeTimeFormat(endTime));

    return slotMinutes >= startMinutes && slotMinutes < endMinutes;
  }

  private _getTimeSlotIndex(time: string): number {
    const normalizedTime = this._normalizeTimeFormat(time);

    for (let i = 0; i < this.filteredTimeSlabs.length; i++) {
      const slabTime = this._normalizeTimeFormat(this.filteredTimeSlabs[i]);
      if (slabTime === normalizedTime) {
        return i;
      }
    }

    const timeMinutes = this._timeToMinutes(normalizedTime);
    let closestIndex = 0;
    let closestDiff = Math.abs(this._timeToMinutes(this._normalizeTimeFormat(this.filteredTimeSlabs[0])) - timeMinutes);

    for (let i = 1; i < this.filteredTimeSlabs.length; i++) {
      const slabMinutes = this._timeToMinutes(this._normalizeTimeFormat(this.filteredTimeSlabs[i]));
      const diff = Math.abs(slabMinutes - timeMinutes);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestIndex = i;
      }
    }

    return closestIndex;
  }

  private _timeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  private _normalizeTimeFormat(time: string): string {
    if (!time) {
      return '00:00'; // Default fallback for undefined/null time
    }

    time = time.trim().toLowerCase();

    if (time.match(/^\d{1,2}:\d{2}$/)) {
      const [hours, minutes] = time.split(':');
      return `${hours.padStart(2, '0')}:${minutes}`;
    }

    if (time.includes('am') || time.includes('pm')) {
      const isPM = time.includes('pm');
      const timeOnly = time.replace(/\s*(am|pm)\s*/g, '');
      const [hours, minutes] = timeOnly.split(':');
      let hour24 = parseInt(hours);

      if (isPM && hour24 !== 12) {
        hour24 += 12;
      } else if (!isPM && hour24 === 12) {
        hour24 = 0;
      }

      return `${hour24.toString().padStart(2, '0')}:${minutes}`;
    }
    return time;
  }

  private _getTodayDateString(): string {
    return this._formatDateString(new Date());
  }

  private _formatDateString(date: Date): string {
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
  }
}
